import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from '../ui/Icon';
import { Icon } from '../ui/Icon';
import Spinner from '../ui/Spinner';
import { toast } from '../ui/Toast';

interface Props {
  onConfirm: () => Promise<void>;
  /**
   * What goes with the place, when something does. Deleting a saved place now
   * deletes its stops too, and a two-word "are you sure?" is not a warning for
   * something that edits the plan.
   */
  warning?: string | null;
}

export default function DeleteVenueButton({ onConfirm, warning }: Props) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-red-200 dark:hover:border-red-800 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer"
      >
        <Icon as={Trash2} size={13} /> {t('common.delete')}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {warning || t('common.areYouSure')}
      </span>
      <button
        onClick={async () => {
          setBusy(true);
          try { await onConfirm(); } catch { toast(t('common.deleteFailed'), 'error'); setBusy(false); setConfirming(false); }
        }}
        disabled={busy}
        className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-50"
      >
        {busy ? <Spinner size="sm" /> : t('common.yesDelete')}
      </button>
      <button
        onClick={() => setConfirming(false)}
        disabled={busy}
        className="px-2.5 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
      >
        {t('common.cancel')}
      </button>
    </div>
  );
}
